import { defineMessages } from 'react-intl';

export const connectedAccountsConnectingMessages = defineMessages({
  'whatIsConnectAccount:question': {
    id: 'simplifiedHelpContent.connectingAccounts.whatIsConnectAccount.question',
    defaultMessage: 'What is Connect Account?',
  },
  'whatIsConnectAccount:answer': {
    id: 'simplifiedHelpContent.connectingAccounts.whatIsConnectAccount.answer',
    defaultMessage:
      'Connect Account lets you link multiple AWS Training and Certification (T&C) accounts you own. Once linked, your training activity, badges, and credentials on skills profile are consolidated into one unified view, accessible from any of your connected accounts.',
  },
  'howToConnect:question': {
    id: 'simplifiedHelpContent.connectingAccounts.howToConnect.question',
    defaultMessage: 'How do I connect my accounts?',
  },
  'howToConnect:answer': {
    id: 'simplifiedHelpContent.connectingAccounts.howToConnect.answer',
    defaultMessage:
      '<ol><li>Log in to skillbuilder.aws</li><li>Navigate to your Profile section.</li><li>Select the option to connect another account.</li><li>Enter the email address associated with the account you want to connect.</li><li>You will be redirected to sign in to that account to verify ownership.</li><li>Once authenticated, the accounts are connected.</li></ol>',
  },
  'differentSignInMethods:question': {
    id: 'simplifiedHelpContent.connectingAccounts.differentSignInMethods.question',
    defaultMessage:
      'Can I connect accounts that use different sign-in methods (e.g., AWS Builder ID and organizational SSO)?',
  },
  'differentSignInMethods:answer': {
    id: 'simplifiedHelpContent.connectingAccounts.differentSignInMethods.answer',
    defaultMessage:
      'Yes. Connect Account supports accounts across different sign-in methods, including AWS Builder ID, organizational SSO, and Amazon Federated login. You simply authenticate to each account using its configured sign-in method to complete the connection.',
  },
  'sameEmailTwoAccounts:question': {
    id: 'simplifiedHelpContent.connectingAccounts.sameEmailTwoAccounts.question',
    defaultMessage: 'What if I have two accounts under the same email address?',
  },
  'sameEmailTwoAccounts:answer': {
    id: 'simplifiedHelpContent.connectingAccounts.sameEmailTwoAccounts.answer',
    defaultMessage:
      'If your email address is associated with more than one T&C account, you will be presented with an option to choose which account you want to connect.',
  },
  'previouslyAddedEmail:question': {
    id: 'simplifiedHelpContent.connectingAccounts.previouslyAddedEmail.question',
    defaultMessage:
      'What happens if I previously added an email address to my T&C profile to aggregate badges?',
  },
  'previouslyAddedEmail:answer': {
    id: 'simplifiedHelpContent.connectingAccounts.previouslyAddedEmail.answer',
    defaultMessage:
      'Any email address previously added to your profile for aggregating AWS Training and Certification badges on Skills profile will appear in the Connect Account section. You will be prompted to authenticate to the associated account to complete the connection. Once linked, the email is represented as a connected account. If you choose to remove this email from the Connect Account section, it will no longer be used to aggregate badges, credentials, or certification data for partner analytics.',
  },
});

export const connectedAccountsConsolidatedMessages = defineMessages({
  'whatDataConsolidated:question': {
    id: 'simplifiedHelpContent.whatGetsConsolidated.whatDataConsolidated.question',
    defaultMessage: 'What data is consolidated when I connect my accounts?',
  },
  'whatDataConsolidated:answer': {
    id: 'simplifiedHelpContent.whatGetsConsolidated.whatDataConsolidated.answer',
    defaultMessage:
      'When you connect your accounts, no data is merged or transferred between them. Your training records remain on the original account where the activity was completed. What changes is your ability to view data across accounts from a single place. Once connected, the following experiences are updated: <ul><li>Training Activity \u2014 You can select any of your connected accounts on the Training Activity page to view the training completed on that account. This is accessible from any account in your connected network.</li><li>Skills Profile \u2014 Your Skills Profile shows a consolidated view of badges and credentials associated with your individual connected accounts.</li><li>Partner Reports \u2014 If you are part of an AWS Partner organization, your partner reports and partner scorecard will reflect certification data from your connected accounts.</li></ul> Your data stays with the account where it was earned. Connected Accounts simply gives you visibility across all of them.',
  },
  'seeFromAnyAccount:question': {
    id: 'simplifiedHelpContent.whatGetsConsolidated.seeFromAnyAccount.question',
    defaultMessage:
      'Will I see my consolidated data from any of my connected accounts?',
  },
  'seeFromAnyAccount:answer': {
    id: 'simplifiedHelpContent.whatGetsConsolidated.seeFromAnyAccount.answer',
    defaultMessage:
      'Once connected, you will see the same unified view of your training history and skills profile regardless of which connected account you log into.',
  },
  'contentAccess:question': {
    id: 'simplifiedHelpContent.whatGetsConsolidated.contentAccess.question',
    defaultMessage:
      'Does connecting accounts give me access to paid or partner content on my other accounts?',
  },
  'contentAccess:answer': {
    id: 'simplifiedHelpContent.whatGetsConsolidated.contentAccess.answer',
    defaultMessage:
      'Content access and entitlements remain tied to the individual account. Connecting accounts does not transfer or share content access. To access paid or partner-specific training, you must log in to the account that holds the relevant subscription or entitlement.',
  },
  'subscriptionsShared:question': {
    id: 'simplifiedHelpContent.whatGetsConsolidated.subscriptionsShared.question',
    defaultMessage:
      'Are my subscriptions or admin roles shared across connected accounts?',
  },
  'subscriptionsShared:answer': {
    id: 'simplifiedHelpContent.whatGetsConsolidated.subscriptionsShared.answer',
    defaultMessage:
      'Subscriptions, admin permissions, and elevated access rights remain tied to the individual account on which they were provisioned. Connected Account consolidates training history and achievements only.',
  },
});

export const connectedAccountsDisconnectingMessages = defineMessages({
  'canRemove:question': {
    id: 'simplifiedHelpContent.disconnectingAccounts.canRemove.question',
    defaultMessage: 'Can I remove an account from my connected network?',
  },
  'canRemove:answer': {
    id: 'simplifiedHelpContent.disconnectingAccounts.canRemove.answer',
    defaultMessage:
      'Yes. You can remove any account from your connected network at any time from the Profile section in Skill Builder.',
  },
  'whatHappensDisconnect:question': {
    id: 'simplifiedHelpContent.disconnectingAccounts.whatHappensDisconnect.question',
    defaultMessage: 'What happens to my data when I disconnect an account?',
  },
  'whatHappensDisconnect:answer': {
    id: 'simplifiedHelpContent.disconnectingAccounts.whatHappensDisconnect.answer',
    defaultMessage:
      'Disconnecting is non-destructive. Each account retains its own data. After disconnecting, the consolidated view will no longer include data from the removed account, but no data is deleted.',
  },
  'canReconnect:question': {
    id: 'simplifiedHelpContent.disconnectingAccounts.canReconnect.question',
    defaultMessage: 'Can I reconnect an account after removing it?',
  },
  'canReconnect:answer': {
    id: 'simplifiedHelpContent.disconnectingAccounts.canReconnect.answer',
    defaultMessage:
      'Yes. You can reconnect a previously removed account by going through the connection flow again from your profile page.',
  },
});

export const connectedAccountsSecurityMessages = defineMessages({
  'howVerifyOwnership:question': {
    id: 'simplifiedHelpContent.securityAndPrivacy.howVerifyOwnership.question',
    defaultMessage:
      'How does Account Connect verify that I own the accounts I am connecting?',
  },
  'howVerifyOwnership:answer': {
    id: 'simplifiedHelpContent.securityAndPrivacy.howVerifyOwnership.answer',
    defaultMessage:
      'You must authenticate to each account you want to connect using its configured sign-in method (AWS Builder ID, enterprise SSO, or Amazon Federated login). This ensures that only the rightful account owner can establish a connection.',
  },
  'whatAdminSees:question': {
    id: 'simplifiedHelpContent.securityAndPrivacy.whatAdminSees.question',
    defaultMessage:
      "What can my employer's Learning Admin see after I connect my accounts?",
  },
  'whatAdminSees:answer': {
    id: 'simplifiedHelpContent.securityAndPrivacy.whatAdminSees.answer',
    defaultMessage:
      "Your employer's Learning Admin continues to see training activity associated with your organization's account only. They do not gain visibility into training completed on your personal or other connected accounts.",
  },
  'leaveEmployer:question': {
    id: 'simplifiedHelpContent.securityAndPrivacy.leaveEmployer.question',
    defaultMessage: 'What happens if I leave my employer?',
  },
  'leaveEmployer:answer': {
    id: 'simplifiedHelpContent.securityAndPrivacy.leaveEmployer.answer',
    defaultMessage:
      'If you connected your work account to your personal account before losing access, your consolidated training history remains visible from your other connected accounts. You can also request a data transfer to move your training records from one account to another.',
  },
  'dataTransferVsConnect:question': {
    id: 'simplifiedHelpContent.securityAndPrivacy.dataTransferVsConnect.question',
    defaultMessage: 'Is data transfer the same as connecting accounts?',
  },
  'dataTransferVsConnect:answer': {
    id: 'simplifiedHelpContent.securityAndPrivacy.dataTransferVsConnect.answer',
    defaultMessage:
      'Connecting accounts creates a link that shows a consolidated view of your training data across accounts; the data stays on each original account. Data transfer physically moves records from one account to another. You can request for data transfer through AWS customer support.',
  },
  'errorConnecting:question': {
    id: 'simplifiedHelpContent.securityAndPrivacy.errorConnecting.question',
    defaultMessage:
      'I am getting an error when trying to connect my account. What should I do?',
  },
  'errorConnecting:answer': {
    id: 'simplifiedHelpContent.securityAndPrivacy.errorConnecting.answer',
    defaultMessage:
      'Account Connect may fail for one of the following reasons: <ul><li>No account found \u2014 No T&C account exists for the email address you entered. Double-check the email and try again.</li><li>Account is inactive or blocked \u2014 The account associated with the email address is no longer active or has been blocked.</li><li>Name mismatch \u2014 The name on the account you are trying to connect does not reasonably match the name on your current account.</li><li>Country/region mismatch \u2014 The accounts you are trying to connect are associated with different countries or regions. Cross-region account linking is not supported at this time.</li><li>Maximum account limit reached \u2014 You can connect up to 4 accounts. To add a new account, remove an existing account from your connected network first.</li><li>Incorrect email address \u2014 Verify that the email address you entered is correct and matches the account you intend to connect.</li></ul>',
  },
});
